/**
 * Аварийная панель «Emergency Hero» (G4, ADR 0009; редизайн 26.07 по
 * docs/Design/radana-emergency-mode-design, артборды 1–2): статус-тикер,
 * пиктограмма+заголовок режима, живая карта в фокус-режиме прямо в панели
 * (прозрачное окно), CTA «→ В укрытие · N мин», чек-лист карточками.
 * Дисклеймер НЕУБИРАЕМЫЙ. Бейдж «Подписано». Анти-паника: никаких мигалок.
 */
import type { LonLat } from '@app/geo'
import { formatDuration, getLang, tr } from '@app/i18n'
import { localized, SPEED_MPS } from '@app/rvf'
import { useEffect, useState } from 'preact/hooks'
import { closeTarget } from '../actions'
import { dropWarmup, warmupAr } from '../ar/permissions'
import { buildEscapeRoute, type EscapeRoute, NO_ESCAPE } from '../emergency/escape-route'
import type { EmergencyFrame } from '../emergency/frame'
import { importChunk } from '../lazy-retry'
import type { ExternalLeg } from '../route-splice'
import {
  arSession,
  emergencyDrill,
  emergencyFrame,
  emergencyHazard,
  emergencyRouteLockTick,
  geoStart,
  graph,
  langTick,
  styleResetTick,
  targetId,
  venue,
  venueMap,
} from '../state'
import { EmergencyChecklist } from './emergency-checklist'
import { modePictogram } from './emergency-icons'
import { EmergencyPass } from './emergency-pass'
import { EmergencyHeroScheme, EmergencyScreenScheme } from './emergency-svg'
import { Glyph } from './glyph'
import { GLYPH_AR } from './glyphs'
import { LangMenu } from './header'
import { BasemapMenu } from './terra-widgets'
import { useExternalEscape } from './use-external-escape'
import { useShelterLayer } from './use-shelter-layer'

/** Тема оверлея по режиму. */
type Theme = 'evacuation' | 'shelter' | 'drill'

function modeTheme(frame: EmergencyFrame): Theme {
  if (frame.mode === 0) return 'evacuation'
  if (frame.mode === 1) return 'shelter'
  return 'drill'
}

function modeLabelKey(frame: EmergencyFrame): Parameters<typeof tr>[0] {
  if (frame.mode === 0) return 'emergency.mode_evacuation'
  if (frame.mode === 1) return 'emergency.mode_shelter'
  return 'emergency.mode_drill'
}

const HAZARD_NAME_KEY = {
  fire: 'emergency.hazard_fire',
  flood: 'emergency.hazard_flood',
  evac: 'emergency.hazard_evac',
  uav: 'emergency.hazard_uav',
} as const

/**
 * Наша полилиния эвакуации для AR-ведения: сеть + финальное прямое плечо.
 * Городское плечо (external) — вне территории, AR его не ведёт.
 */
function escapeArPolyline(escapeRoute: EscapeRoute): readonly LonLat[] | null {
  const pts = [...(escapeRoute.plan?.polyline ?? []), ...(escapeRoute.direct ?? [])]
  return pts.length >= 2 ? pts : null
}

/** Кнопка AR в карте ЧС (спека 13 §эвакуация): тот же AR-движок, красная
 *  тема, авто не выше split — полный AR в ЧС только вручную. */
function EvacArButton({ frame, escapeRoute }: { frame: EmergencyFrame; escapeRoute: EscapeRoute }) {
  const polyline = escapeArPolyline(escapeRoute)
  if (!polyline || arSession.value === 'evac') return null
  const start = (): void => {
    void (async () => {
      await warmupAr(true) // разрешения строго в тапе (iOS)
      const m = await importChunk(() => import('../ar'))
      await m.startEvacAr(
        polyline,
        frame.mode === 1 ? 'emergency.cta_shelter' : 'emergency.cta_exit',
      )
    })()
  }
  return (
    <button class="em-ar-btn" type="button" onClick={start}>
      <Glyph svg={GLYPH_AR} />
      {tr('ar.choice_ar')}
    </button>
  )
}

/** Режим просмотра карты ЧС: водяной знак учений, «назад», подсказка выбора, памятка. */
function EmergencyMapBar({
  frame,
  hazard,
  escapeRoute,
  shelterId,
  onBack,
}: {
  frame: EmergencyFrame
  hazard: import('../emergency/drill').DrillHazard | null
  escapeRoute: EscapeRoute
  shelterId: string | null
  onBack: () => void
}) {
  // Выбор стиля карты доступен и в ЧС (фидбек владельца 26.07).
  const [styleOpen, setStyleOpen] = useState(false)
  return (
    <>
      {/* Движок карты не загрузился — вместо пустоты схема по данным площадки. */}
      <EmergencyScreenScheme route={escapeRoute} shelterId={shelterId} />
      {/* Колонка инструментов НИЖЕ зума MapLibre (не перекрывать +/-):
          памятка · язык · стиль карты · передать соседу */}
      <div class="em-map-tools">
        {hazard && <EmergencyChecklist hazard={hazard} variant="map" />}
        <EmergencyPass variant="map" />
        <LangMenu />
        <button
          type="button"
          class="em-style-btn"
          aria-label={tr('basemap.title')}
          title={tr('basemap.title')}
          onClick={() => setStyleOpen((v) => !v)}
        >
          ◐
        </button>
        {styleOpen && <BasemapMenu onClose={() => setStyleOpen(false)} />}
      </div>
      {emergencyDrill.value && (
        <div class="em-drill-watermark--map" aria-hidden="true">
          {tr('emergency.drill_watermark')}
        </div>
      )}
      <aside class="em-map-bar" aria-label={tr(modeLabelKey(frame))}>
        <button class="em-back-btn" type="button" onClick={onBack}>
          {tr('emergency.back_to_alert')}
        </button>
        <EvacArButton frame={frame} escapeRoute={escapeRoute} />
        {frame.mode === 1 && <span class="em-pick-hint">{tr('drill.pick_hint')}</span>}
      </aside>
      <RouteLockHint />
    </>
  )
}

/** Подсказка «маршруты только к укрытиям»: всплывает на тап по обычному
 *  зданию во время ЧС (selectTarget заблокирован), гаснет через 2.6 с. */
function RouteLockHint() {
  const tick = emergencyRouteLockTick.value
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (tick === 0) return
    setVisible(true)
    const timer = setTimeout(() => setVisible(false), 2600)
    return () => clearTimeout(timer)
  }, [tick])
  if (!visible) return null
  return <p class="em-lock-hint">{tr('emergency.routes_locked')}</p>
}

/** Статус-тикер (верхняя строка героя): сценарий + бейдж режима. */
function HeroTicker({
  frame,
  hazard,
}: {
  frame: EmergencyFrame
  hazard: import('../emergency/drill').DrillHazard | null
}) {
  const scenario = hazard ? tr(HAZARD_NAME_KEY[hazard]) : tr(modeLabelKey(frame))
  return (
    <header class="em-ticker">
      <span class="em-ticker-dot" aria-hidden="true" />
      <span class="em-ticker-text">
        {tr('emergency.scenario')}: {scenario}
      </span>
      <span class="em-ticker-badge">{tr(modeLabelKey(frame))}</span>
      <LangMenu />
    </header>
  )
}

/**
 * Пояснение под окном, когда нашей сети до укрытия нет: путь по городским
 * дорогам (внешнее плечо) либо честный «по прямой» (крайний фолбэк).
 */
function RouteStatusNote({
  escapeRoute,
  external,
}: {
  escapeRoute: EscapeRoute
  external: ExternalLeg | null
}) {
  if (escapeRoute.plan) return null
  if (external)
    return <p class="em-no-route em-no-route--window">{tr('emergency.external_route')}</p>
  if (escapeRoute.direct)
    return <p class="em-no-route em-no-route--window">{tr('emergency.direct_route')}</p>
  return null
}

/** CTA «→ В укрытие · N мин»: время из плана (пешеход) или внешнего плеча. */
function HeroCta({
  frame,
  escapeRoute,
  external,
  onOpen,
}: {
  frame: EmergencyFrame
  escapeRoute: EscapeRoute
  external: ExternalLeg | null
  onOpen: () => void
}) {
  const plan = escapeRoute.plan
  if (!plan && !escapeRoute.direct && !external) return null
  const label = frame.mode === 1 ? tr('emergency.cta_shelter') : tr('emergency.cta_exit')
  const eta = plan
    ? formatDuration(plan.distanceM / SPEED_MPS.foot)
    : external
      ? formatDuration(external.durationS)
      : null
  return (
    <button class="em-cta" type="button" onClick={onOpen}>
      → {label}
      {eta ? ` · ${eta}` : ''}
    </button>
  )
}

export function EmergencyPanel() {
  langTick.value // подписка на смену языка
  const frame = emergencyFrame.value
  const v = venue.value
  const g = graph.value
  const map = venueMap.value

  // Пересчёт при появлении геолокации: до неё маршрут строится от центра
  // площадки, после — от человека.
  const geo = geoStart.value
  const [mapVisible, setMapVisible] = useState(false)
  const [escapeRoute, setEscapeRoute] = useState<EscapeRoute>(NO_ESCAPE)
  // Выбранное укрытие (тап по маркеру); null = ближайшее автоматически.
  const [shelterId, setShelterId] = useState<string | null>(null)

  // Строим маршрут эвакуации при появлении всех данных (и при перевыборе).
  // Сеть не доводит до выбранного укрытия → честный прямой пунктир (не молчим).
  useEffect(() => {
    if (!frame || !v || !g) return
    setEscapeRoute(buildEscapeRoute(g, v, frame, shelterId, geoStart.value))
  }, [frame, v, g, shelterId, geo])

  // Наших дорожек к укрытию нет → городское плечо от «моего места» (внешний
  // навигатор); прямой пунктир — крайний фолбэк. Логика — в хуке.
  const external = useExternalEscape({ v, frame, hasPlan: !!escapeRoute.plan, shelterId })

  // Обновляем маршрут на карте при смене плана (и после смены стиля карты).
  const resetTick = styleResetTick.value
  useEffect(() => {
    map?.setEmergencyRoute(escapeRoute.plan, escapeRoute.direct, external?.polyline ?? null)
    return () => {
      // Сброс при размонтировании панели (EMERGENCY завершён).
      map?.setEmergencyRoute(null)
    }
  }, [map, escapeRoute, external, resetTick])

  // Вход в ЧС закрывает открытую карточку/маршрут обычного режима: на экране
  // не должно остаться двух конкурирующих маршрутов. Разогретая для AR камера
  // (выбор на экране ориентации) тоже гасится — под баннером ЧС ей гореть нечего.
  useEffect(() => {
    if (!frame) return
    dropWarmup()
    if (targetId.value) closeTarget()
  }, [frame])

  // ЧС завершён (панель размонтируется) — AR-ведение эвакуации не переживает его.
  useEffect(
    () => () => {
      if (arSession.value === 'evac') {
        void importChunk(() => import('../ar')).then((m) => m.stopEvacAr())
      }
    },
    [],
  )

  // Фокус-режим (артборд 2): обычное — полупрозрачно, ярко — укрытия и маршрут.
  useEffect(() => {
    if (!frame) return
    map?.setEmergencyFocus(true)
    return () => map?.setEmergencyFocus(false)
  }, [map, frame, resetTick])

  // Слой укрытий с занятостью — всё время ЧС (карта видна в окне героя).
  useShelterLayer({
    map,
    v,
    mapVisible: true,
    frame,
    shelterId,
    escapePlan: escapeRoute.plan,
    onPick: setShelterId,
  })

  // Камера: маршрут (сеть + прямое плечо) и укрытия целиком — сразу.
  useEffect(() => {
    if (!map) return
    const routePts = [
      ...(escapeRoute.plan?.polyline ?? []),
      ...(external?.polyline ?? []),
      ...(escapeRoute.direct ?? []),
    ]
    if (!routePts.length) return
    const shelterPts = frame?.mode === 1 ? (v?.evacuation?.shelters ?? []).map((s) => s.point) : []
    map.fitPoints([...routePts, ...shelterPts])
  }, [map, escapeRoute, external, frame, v])

  if (!frame || !v) return null

  const theme = modeTheme(frame)
  const venueName = localized(v.venue.name, getLang(), v.venue.default_language)

  // Текст сообщения из venue.evacuation.messages по msgId кадра.
  const msgId = frame.msgId
  const msg = msgId ? v.evacuation?.messages?.find((m) => m.id === msgId) : null
  const headline = msg ? localized(msg.headline, getLang(), v.venue.default_language) : null
  const instruction = msg ? localized(msg.instruction, getLang(), v.venue.default_language) : null

  const hazard = emergencyHazard.value
  if (mapVisible) {
    return (
      <EmergencyMapBar
        frame={frame}
        hazard={hazard}
        escapeRoute={escapeRoute}
        shelterId={shelterId}
        onBack={() => setMapVisible(false)}
      />
    )
  }

  const noRoute = !escapeRoute.plan && !escapeRoute.direct && !external

  return (
    <div
      class={`em-hero em-hero--${theme}`}
      role="alertdialog"
      aria-modal="false"
      aria-label={tr(modeLabelKey(frame))}
    >
      {/* Жёлтая рамка + вотемарк + баннер учений */}
      {(theme === 'drill' || emergencyDrill.value) && (
        <>
          <div class="em-drill-watermark" aria-hidden="true">
            {tr('emergency.drill_watermark')}
          </div>
          <div class="em-drill-banner">{tr('emergency.mode_drill')} · ÕPPUS</div>
        </>
      )}

      <HeroTicker frame={frame} hazard={hazard} />

      <div class="em-hero-top">
        <div class="em-hero-aura" aria-hidden="true" />
        <span
          class="em-hero-pictogram"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: modePictogram(frame.mode) }}
        />
        <h1 class="em-hero-title">{tr(modeLabelKey(frame))}</h1>
        <p class="em-hero-instruction">{headline || instruction || tr('emergency.no_route')}</p>
        {headline && instruction && <p class="em-hero-sub">{instruction}</p>}
        <p class="em-signed">
          {emergencyDrill.value
            ? tr('emergency.drill_unsigned')
            : `✓ ${tr('emergency.signed_by', { name: venueName })}`}
        </p>
      </div>

      {/* Памятка — над окном карты (фидбек 26.07: выше и удобнее);
          раскрытая — плавающая, таскается за шапку, позиция запоминается. */}
      {hazard && <EmergencyChecklist hazard={hazard} />}

      {/* Прозрачное окно на живую карту в фокус-режиме; CTA на нижней кромке. */}
      <div class="em-hero-window">
        {/* Линии эвакуации раньше тайлов: пока карты нет — схема по данным. */}
        <EmergencyHeroScheme route={escapeRoute} shelterId={shelterId} />
        <RouteStatusNote escapeRoute={escapeRoute} external={external} />
        <RouteLockHint />
        <HeroCta
          frame={frame}
          escapeRoute={escapeRoute}
          external={external}
          onOpen={() => setMapVisible(true)}
        />
        {noRoute && (
          <button class="em-cta em-cta--calm" type="button" onClick={() => setMapVisible(true)}>
            {tr('emergency.map_btn')}
          </button>
        )}
      </div>

      <div class="em-hero-bottom">
        {/* Рядом стоящему человеку — тот же режим по QR с экрана */}
        <EmergencyPass />
        {/* Неубираемый дисклеймер — всегда внизу */}
        <div class="em-disclaimer" role="note">
          {tr('emergency.disclaimer')}
        </div>
      </div>
    </div>
  )
}
